
import { Link } from "react-router-dom";
import { Download, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { toast } from "sonner";
import { Attachment } from "@/lib/types";

interface AttachmentsListProps {
  contractId: string;
  attachments: Attachment[];
}

export function AttachmentsList({ contractId, attachments }: AttachmentsListProps) {
  const handleDownloadFile = (url: string, fileName: string) => {
    try {
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      
      toast.success(`Downloading ${fileName}`);
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download file. Please try again.");
    }
  };

  const handleViewFile = (url: string, fileName: string) => {
    try {
      window.open(url, '_blank');
      toast.success(`Opening ${fileName}`);
    } catch (error) {
      console.error("Error opening file:", error);
      toast.error("Could not open the file. Please try downloading instead.");
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Attachments</CardTitle>
        <CardDescription>Documents related to this contract</CardDescription>
      </CardHeader>
      <CardContent>
        {attachments.length === 0 ? (
          <div className="text-center py-10 text-muted-foreground">
            <p>No attachments uploaded</p>
            <Button variant="link" asChild>
              <Link to={`/contracts/${contractId}/edit`}>
                Add attachments
              </Link>
            </Button>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {attachments.map((attachment) => (
              <div 
                key={attachment.id} 
                className="border rounded-md p-4 flex flex-col"
              >
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-sm truncate">{attachment.name}</h4>
                </div>
                <div className="flex items-center mt-1">
                  <Badge variant="outline" className="text-xs">
                    {attachment.type.split('/')[1]?.toUpperCase() || attachment.type}
                  </Badge>
                  <span className="text-xs text-muted-foreground ml-2">
                    {Math.round(attachment.size / 1024)} KB
                  </span>
                </div>
                <div className="mt-4 flex justify-end gap-2">
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="h-8 px-2" 
                    onClick={() => handleViewFile(attachment.url, attachment.name)}
                  >
                    <ExternalLink className="h-3.5 w-3.5 mr-1" />
                    View
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="h-8 px-2" 
                    onClick={() => handleDownloadFile(attachment.url, attachment.name)}
                  >
                    <Download className="h-3.5 w-3.5 mr-1" />
                    Download
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
